import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { SqlCopySource } from 'app/model/pipeline/copysource/sql.copysource';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { SqlTypeCopySource } from 'app/model/pipeline/copysource/sql-type.copysource';
import { Owned } from 'app/model/base/named';
import moment from 'moment-timezone';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';

export const sqlSourceAdapts = {
    ...commonCopySourceAdapts,
    sqlReaderQuery: '*',
    sqlReaderStoredProcedureName: '*',
    storedProcedureParameters: '*',
    queryTimeout: '*',
    isolationLevel: '*',
    partitionOption: '*',
    partitionSettings: {
        partitionColumnName: '*',
        partitionUpperBound: '*',
        partitionLowerBound: '*'
    }
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlCopySource),
    asynchronous: true,
    adapts: sqlSourceAdapts
})
export class SqlCopySourceAdapter extends CopySourceAdapter<SqlCopySource, Model.SqlSource> {
    protected override async _toResource(model: SqlCopySource): Promise<Model.SqlSource> {
        const result: Model.SqlSource = this.getCommonCopySourceProperties(model, Metamodel.modelOf(model).name);
        if (model.mode === 'query') {
            result.sqlReaderQuery = ValueAdapter.toResource(model, SqlTypeCopySource.READER_QUERY);
        } else if (model.mode === 'storedProcedure') {
            result.sqlReaderStoredProcedureName = ValueAdapter.toResource(
                model,
                SqlTypeCopySource.READER_STORED_PROCEDURE_NAME
            );

            if (model.readerStoredProcedureParameters != null && model.readerStoredProcedureParameters.length > 0) {
                const params: { [propertyName: string]: Model.StoredProcedureParameter } = {};

                model.readerStoredProcedureParameters.forEach((p) => {
                    params[p.name] = {
                        type: p.type,
                        value: ValueAdapter.toResource(p, ParameterConfig.VALUE)
                    };
                });
                result.storedProcedureParameters = params;
            } else {
                result.storedProcedureParameters = ValueAdapter.toResource(
                    model,
                    SqlTypeCopySource.READER_STORED_PROCEDURE_PARAMETERS
                );
            }

            // clear [] to keep the current behavior
            if (
                result.storedProcedureParameters &&
                Array.isArray(result.storedProcedureParameters) &&
                result.storedProcedureParameters.length === 0
            ) {
                result.storedProcedureParameters = undefined;
            }
        }

        try {
            result.queryTimeout =
                typeof model.queryTimeout === 'number'
                    ? moment.duration(model.queryTimeout, 'minutes').format('hh:mm:ss', {
                          trim: false
                      })
                    : ValueAdapter.toResource(model, SqlCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert queryTimeout into TimeSpan');
        }

        result.isolationLevel = ValueAdapter.toResource(model, SqlCopySource.ISOLATION_LEVEL);

        result.partitionOption = ValueAdapter.toResource(model, SqlCopySource.PARTITION_OPTION);
        if (
            SqlCopySource.PARTITION_OPTION.templateValue(model) != null ||
            model.partitionOption === partitionOptionConst.dyamicRange
        ) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(model, SqlCopySource.PARTITION_COLUMN_NAME),
                partitionUpperBound: ValueAdapter.toResource(model, SqlCopySource.PARTITION_UPPER_BOUND),
                partitionLowerBound: ValueAdapter.toResource(model, SqlCopySource.PARTITION_LOWER_BOUND)
            };
        }
        return result;
    }

    protected override async _toModel(resource: Model.SqlSource, copyActivity: CopyActivity): Promise<SqlCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            Metamodel.modelByName(resource.type),
            resource,
            copyActivity
        ) as SqlCopySource;
        if (resource.sqlReaderQuery) {
            ValueAdapter.toModel(result, SqlTypeCopySource.READER_QUERY, resource.sqlReaderQuery);
            result.mode = 'query';
        } else if (resource.sqlReaderStoredProcedureName) {
            ValueAdapter.toModel(
                result,
                SqlTypeCopySource.READER_STORED_PROCEDURE_NAME,
                resource.sqlReaderStoredProcedureName
            );

            if (resource.storedProcedureParameters) {
                if (
                    typeof resource.storedProcedureParameters === 'object' &&
                    resource.storedProcedureParameters.type === 'Expression'
                ) {
                    ValueAdapter.toModel(
                        result,
                        SqlTypeCopySource.READER_STORED_PROCEDURE_PARAMETERS,
                        resource.storedProcedureParameters
                    );
                } else {
                    result.readerStoredProcedureParameters = Object.keys(resource.storedProcedureParameters).map(
                        (key) => {
                            const item = Owned.create<ParameterConfig>(ParameterConfig.MODEL, copyActivity);
                            item.name = key;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const originItem = resource.storedProcedureParameters as any;
                            item.type = originItem[key] != null ? originItem[key].type : undefined;
                            ValueAdapter.toModel(
                                item,
                                ParameterConfig.VALUE,
                                originItem[key] != null ? originItem[key].value : undefined
                            );
                            return item;
                        }
                    );
                }
            }
            result.mode = 'storedProcedure';
        }
        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asMinutes();
                } else {
                    ValueAdapter.toModel(result, SqlCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert queryTimeout into number');
        }
        ValueAdapter.toModel(result, SqlCopySource.ISOLATION_LEVEL, resource.isolationLevel);

        if (resource.partitionOption != null) {
            ValueAdapter.toModel(result, SqlCopySource.PARTITION_OPTION, resource.partitionOption);
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                SqlCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                SqlCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                SqlCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }
        return result;
    }
}
